import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import authStore from 'store/auth/auth'
import Switch from 'ui/atoms/Switch/Switch'

import { systemFeatureFlagActive } from 'utils/systemUtils'
import { useSnapshot } from 'valtio'

import { OptimizeViewStore } from 'views/OptimizeView/store/optimizeViewStore'
import { useShouldShowCustomDatepicker } from 'views/OptimizeView/utils/useShouldShowCustomDatepicker'

type ZoomResolutionState = OptimizeViewStore['zoomResolutionSwitch']['type']

interface DataResolutionProps {
  onClick: (value: ZoomResolutionState) => void
  value: ZoomResolutionState
}

export default function DataResolutionSwitch(props: DataResolutionProps): ReactElement {
  const { t } = useTranslation()
  const { value, onClick } = props
  const { activeSystem } = useSnapshot(authStore)

  const shouldShowCustomDatepicker = useShouldShowCustomDatepicker()
  const systemHasFiveDaysOptimizationPeriod = activeSystem?.default_optimization_period === 119 // 5 days * 24h -1h
  const showThreeDayOption = systemFeatureFlagActive(activeSystem, 'optimize_datepicker_three_days_option')

  const baseItems = [
    {
      value: systemHasFiveDaysOptimizationPeriod ? `five_days` : `week`,
      label: systemHasFiveDaysOptimizationPeriod ? t(`5 days`) : t(`Week`),
    },
    showThreeDayOption
      ? {
          value: `three_days`,
          label: t(`3 days`),
        }
      : undefined,
    {
      value: `day`,
      label: t(`Day`),
    },
  ]

  const items: { value: string; label: string }[] = []
  if (shouldShowCustomDatepicker) {
    items.push({
      value: `custom`,
      label: t(`Custom`),
    })
  }
  items.push(...baseItems.filter((item): item is { value: string; label: string } => item !== undefined))

  // The Switch component stores the selected tabIndex in its internal state.
  // Given that we have a dynamic number of items we need to reset the state
  // when the items change.
  const key = items.map((item) => item.value).join(':')

  return <Switch key={key} value={value} onClick={onClick} items={items} />
}
